import type {
  AvConnectionInfo,
  AvRefreshReasons,
  AvStateType,
  ConnectionQualityLevels,
  DeviceStateRepresentationType,
  ReducedAvStateType,
} from "@minervaproject/avsubsystem-core";

import { PreclassCheckResultType } from "./preclass-check";
import { WebsocketStatusType } from "./pubsub";

export enum MonitoringEventTypeType {
  AV_CONNECTION_QUALITY_CHANGED = "av-connection-quality-changed",
  AV_REFRESH = "av-refresh",
  DEVICES_ERROR = "devices-error",
  DEVICES_SELECTED = "devices-selected",
  ENTER_CLASS = "enter-class",
  FAILED_CONNECTION_ALERT = "failed-connection-alert",
  RELOAD_STREAM = "reload-stream",
  STREAM_FAILED = "stream-failed",
  STREAM_STARTED = "stream-started",
  // Not part of the original enum - to be moved when we use AvMonitoringEventTypeType from avsubsystem
  AUDIO_TOGGLED = "audio-toggled",
  AV_CHECKIN = "av-checkin",
  AV_PRESENT_IN_ANOTHER_TAB = "av-present-in-another-tab",
  BROWSER_TOO_OLD = "browser-too-old",
  FAILED_API_REQUEST = "failed-api-request",
  FORCE_MUTE_ALL = "force-mute-all",
  PAGE_WORKSHEET_LOAD_ERROR = "page-worksheet-load-error",
  PING_STATS = "ping-stats",
  POOR_CONNECTION_WARNING = "poor-connection-warning",
  PRECLASS_CHECK_FAIL = "preclass-check-fail",
  PRECLASS_CHECK_STARTED = "preclass-check-started",
  PRECLASS_CHECK_SUCCESS = "preclass-check-success",
  VIDEO_TOGGLED = "video-toggled",
  WEBSOCKET_CONNECTED = "websocket-connected",
  WEBSOCKET_DISCONNECTED = "websocket-disconnected",
  WEBSOCKET_ERROR = "websocket-error",
}

export type BaseMonitoringEventType = {
  userId: string;
  userName: string;
  classUserId: string;
  classId: string;
  classType: "virtual" | "in-person";
  sectionId: string;
  sectionTitle: string;
  courseCode: string;
  orgId: string;
  orgShortName: string;
};

export type MonitoringEventContextDataMap = {
  [K in MonitoringEventTypeType]: K extends keyof MonitoringEventContextDataMap
    ? MonitoringEventContextDataMap[K]
    : never;
} & {
  [MonitoringEventTypeType.AUDIO_TOGGLED]: {
    isAudioEnabled: boolean;
    reason:
      | "user_toggle"
      | "auto_muted_on_join"
      | "auto_muted_on_refresh"
      | "auto_muted_from_breakout"
      | "system";
  };
  [MonitoringEventTypeType.AV_CONNECTION_QUALITY_CHANGED]: {
    avState: ReducedAvStateType;
    connectionQuality: ConnectionQualityLevels;
  };
  [MonitoringEventTypeType.AV_CHECKIN]: {
    avState: ReducedAvStateType;
    publisherStats: AvConnectionInfo;
  };
  [MonitoringEventTypeType.AV_PRESENT_IN_ANOTHER_TAB]: Record<string, never>;
  [MonitoringEventTypeType.AV_REFRESH]: {
    avState: ReducedAvStateType;
    reason: AvRefreshReasons;
  };
  [MonitoringEventTypeType.BROWSER_TOO_OLD]: Record<string, never>;
  [MonitoringEventTypeType.DEVICES_ERROR]: {
    avState: ReducedAvStateType;
    devicesState: DeviceStateRepresentationType;
  };
  [MonitoringEventTypeType.DEVICES_SELECTED]: {
    devicesState: DeviceStateRepresentationType;
  };
  [MonitoringEventTypeType.ENTER_CLASS]: {
    userAgent: string;
    navigationTimingMetrics: NavigationTimingMetricsType | null;
  };
  [MonitoringEventTypeType.FAILED_API_REQUEST]: {
    model: string;
    action: string;
    errorMessage: string;
  };
  [MonitoringEventTypeType.FAILED_CONNECTION_ALERT]: {
    avState: ReducedAvStateType;
    reason: string;
  };
  [MonitoringEventTypeType.FORCE_MUTE_ALL]: Record<string, never>;
  [MonitoringEventTypeType.PAGE_WORKSHEET_LOAD_ERROR]: {
    pageId: string;
    docId: string;
  };
  [MonitoringEventTypeType.PING_STATS]: {
    connection: "pubsub" | "sharedb";
    pingRtt: {
      isHigh: boolean;
      isLow: boolean;
      median: number | undefined;
      min: number;
      max: number;
    };
  };
  [MonitoringEventTypeType.POOR_CONNECTION_WARNING]: Record<string, never>;
  [MonitoringEventTypeType.PRECLASS_CHECK_FAIL]: PreclassCheckResultType;
  [MonitoringEventTypeType.PRECLASS_CHECK_STARTED]: Record<string, never>;
  [MonitoringEventTypeType.PRECLASS_CHECK_SUCCESS]: PreclassCheckResultType;
  [MonitoringEventTypeType.RELOAD_STREAM]: {
    avState: ReducedAvStateType;
    targetUserId: string;
  };
  [MonitoringEventTypeType.STREAM_FAILED]: {
    avState: ReducedAvStateType;
    targetUserId: string;
    isScreenshare: boolean;
    streamError: string;
  };
  [MonitoringEventTypeType.STREAM_STARTED]: {
    fullAvState: AvStateType;
  };
  [MonitoringEventTypeType.VIDEO_TOGGLED]: {
    isVideoEnabled: boolean;
  };
  [MonitoringEventTypeType.WEBSOCKET_CONNECTED]: {
    connection?: "pubsub" | "sharedb";
  };
  [MonitoringEventTypeType.WEBSOCKET_DISCONNECTED]: {
    connection?: "pubsub" | "sharedb";
    reason: string;
  };
  [MonitoringEventTypeType.WEBSOCKET_ERROR]: {
    connection?: "pubsub" | "sharedb";
    websocketStatus: WebsocketStatusType;
  };
};

export type MonitoringEventType = BaseMonitoringEventType &
  {
    [K in keyof MonitoringEventContextDataMap]: {
      type: K;
      contextData: MonitoringEventContextDataMap[K];
    };
  }[keyof MonitoringEventContextDataMap];

export type EventMonitoringStatsType = {
  targetClassUserIds: string[];
  userIds: string[];
};

export type MonitoringMessageType = {
  type: "monitoring-update";
  sectionId: string;
  eventType: MonitoringEventTypeType;
  data: EventMonitoringStatsType;
};

export type SectionMonitoringStatsType = {
  [eventType: string]: EventMonitoringStatsType;
};

export type MonitoringStatsType = {
  stats: {
    [sectionId: string]: SectionMonitoringStatsType;
  };
};

export type MonitoringEventStatsType = {
  count: number;
  nAffectedUsers: number;
  mean: number | null;
  percentAffectedUsers: number | null;
  formattedMean: string;
};

export type UserStateStatsType = {
  affectedUsers: string[];
  percentAffectedUsers: number | null;
  formattedPercentage: string;
};

export interface NavigatorConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
}

export type NavigationTimingMetricsType = {
  timestamp: string;
  dns_lookup_ms: number;
  tcp_connection_ms: number;
  ssl_handshake_ms: number;
  request_response_ms: number;
  total_network_ms: number;
  dom_interactive_ms: number;
  dom_content_loaded_ms: number;
  dom_complete_ms: number;
  total_load_ms: number;
  transfer_size_bytes: number;
  encoded_size_bytes: number;
  decoded_size_bytes: number;
  connection: NavigatorConnection;
  performance_score: number;
  network_quality: string;
};
